package fr.arcplex.network.impl.legacy

import fr.arcplex.network.http.JsonMap
import fr.arcplex.network.model.SearchPage
import fr.arcplex.network.model.parseRailItems
import io.ktor.http.encodeURLParameter

/**
 * Search-page contract of the legacy server.
 *
 * Every query change re-requests the `btn_OK` url (which holds the
 * [SEARCH_PLACEHOLDER] marker); the server answers with `action=refreshLine`,
 * `target_to_refresh` = the results rail and `str_recherche` = the query that
 * produced the results.
 */

const val SEARCH_PLACEHOLDER = "#STRRECHERCHE#"

/** The server expects `__` instead of an empty query. */
const val EMPTY_SEARCH_QUERY = "__"

/**
 * Query sent to the server: `__` when empty, plus the CRLF the originals append
 * when the user validates the field.
 */
fun searchQueryPayload(query: String, validate: Boolean = false): String {
    val base = query.ifEmpty { EMPTY_SEARCH_QUERY }
    return if (validate) "$base\r\n" else base
}

/** Replaces [SEARCH_PLACEHOLDER] with the encoded query. */
fun buildSearchUrl(actionUrl: String, query: String, validate: Boolean = false): String =
    actionUrl.replace(SEARCH_PLACEHOLDER, searchQueryPayload(query, validate).encodeURLParameter())

/**
 * Some pages publish their rail urls as `getPage?idpage=...&type=...` (search
 * page: margin/result/keyboard). Those urls answer with a whole page; the rail
 * objects are served by `getLineContent` with the same query string.
 */
internal fun lineContentUrl(url: String): String? {
    val base = url.substringBefore('?')
    if (!base.endsWith("/getPage")) return null
    val query = url.substringAfter('?', "")
    val names = query.split('&').filter { '=' in it }.map { it.substringBefore('=') }
    if ("idpage" !in names || "type" !in names) return null
    return base.removeSuffix("/getPage") + "/getLineContent?" + query
}

/**
 * Reads a `refreshLine` answer.
 *
 * Only the rail named by `target_to_refresh` is described. Answers produced by
 * another query (stale answers) and non-refreshLine answers return null.
 */
internal fun parseSearchResponse(response: JsonMap, query: String): SearchPage? {
    if (response.getString("action") != "refreshLine") return null

    // Some servers always answer `str_recherche: ""`; only a non-blank value
    // is an actual echo and can prove the answer stale.
    val serverQuery = response.getString("str_recherche")?.takeIf { it.isNotBlank() }
    if (serverQuery != null &&
        serverQuery != query &&
        serverQuery != searchQueryPayload(query)
    ) {
        return null
    }

    val target = response.getString("target_to_refresh") ?: return null
    return SearchPage(target, parseRailItems(response.get("content")?.get("objects")))
}
